import { useCallback } from 'react';
import type { TemplateSigningField } from '../types/template';
import { useDsTranslate } from '../context/DsProvider';
import { COUNTRY_CODES } from '../utils/countryCodes';
import { CURRENCIES } from '../utils/currencies';
import { hasDefaultValue } from '../utils/fieldLock';
import Select from './ui/Select';
import styles from './FieldPropertiesPanel.module.scss';

interface RecipientInfo {
  name: string;
  role: string;
  signingOrder?: number;
}

interface FieldPropertiesPanelProps {
  field: TemplateSigningField;
  fieldIndex: number;
  recipientCount: number;
  recipients?: RecipientInfo[];
  onUpdate: (index: number, updates: Partial<TemplateSigningField>) => void;
  onDelete: (index: number) => void;
  /**
   * When true, suppress the "Prefilled by author" option.
   * Used in the envelope wizard — author fields belong on the template, not the envelope.
   */
  disableAuthorMode?: boolean;
}

const AUTHOR_UNSUPPORTED: ReadonlySet<TemplateSigningField['fieldType']> = new Set([
  'SIGNATURE',
  'INITIALS',
  'DATE_SIGNED',
]);

export function FieldPropertiesPanel({
  field,
  fieldIndex,
  recipientCount,
  recipients,
  onUpdate,
  onDelete,
  disableAuthorMode = false,
}: FieldPropertiesPanelProps) {
  const t = useDsTranslate();
  const isAuthor = field.filledBy === 'AUTHOR';
  const actor = isAuthor
    ? t('K_DS_FIELD_ACTOR_AUTHOR', 'author')
    : t('K_DS_FIELD_ACTOR_SIGNER', 'signer');
  const canBeAuthorPrefilled = !AUTHOR_UNSUPPORTED.has(field.fieldType) && !disableAuthorMode;
  const hasDefault = hasDefaultValue(field);

  // Atomically clear `isReadOnly` when the default becomes empty — prevents
  // the stale `{ isReadOnly: true, defaultValue: '' }` shape that
  // `isEffectivelyLocked` has to defend against downstream.
  const setDefault = useCallback(
    (newValue: string) => {
      const isEmpty = newValue.trim() === '';
      if (isEmpty && field.isReadOnly) {
        onUpdate(fieldIndex, { defaultValue: newValue, isReadOnly: false });
      } else {
        onUpdate(fieldIndex, { defaultValue: newValue });
      }
    },
    [field.isReadOnly, fieldIndex, onUpdate],
  );

  return (
    <div className={styles.panel}>
      <div className={styles.header}>
        <span className={styles.readOnly}>
          {field.fieldType}
          {isAuthor && (
            <span className={styles.authorBadge}>
              {' '}&bull; {t('K_DS_FIELD_PREFILLED_BADGE', 'Prefilled')}
            </span>
          )}
        </span>
        <button className={styles.deleteBtn} onClick={() => onDelete(fieldIndex)}>
          {t('K_DELETE', 'Delete')}
        </button>
      </div>

      <div className={styles.fields}>
        <div className={styles.field}>
          <label>{t('K_LABEL_LABEL', 'Label')}</label>
          <input
            type="text"
            value={field.label ?? ''}
            onChange={(e) => onUpdate(fieldIndex, { label: e.target.value })}
          />
        </div>

        <div className={styles.field}>
          <label>{t('K_DS_FILLED_BY', 'Filled by')}</label>
          {disableAuthorMode && isAuthor ? (
            // In the envelope wizard, author-prefilled fields are inherited from the
            // template and cannot be reassigned.
            <div className={styles.readOnly}>
              {t('K_DS_FIELD_AUTHOR_INHERITED', 'Prefilled by author (from template)')}
            </div>
          ) : (
            <select
              value={isAuthor ? 'AUTHOR' : String(field.recipientIndex)}
              onChange={(e) => {
                if (e.target.value === 'AUTHOR') {
                  onUpdate(fieldIndex, {
                    filledBy: 'AUTHOR',
                    recipientIndex: 0,
                  });
                } else {
                  onUpdate(fieldIndex, {
                    filledBy: 'SIGNER',
                    recipientIndex: Number(e.target.value),
                  });
                }
              }}
            >
              {canBeAuthorPrefilled && (
                <option value="AUTHOR">
                  {t(
                    'K_DS_FIELD_AUTHOR_OPTION',
                    'Prefilled by author (sender fills before sending)',
                  )}
                </option>
              )}
              {Array.from({ length: recipientCount }, (_, i) => {
                const r = recipients?.[i];
                const slotLabel = `${t('K_DS_SIGNER', 'Signer')} ${i + 1}`;
                const orderSuffix = r?.signingOrder
                  ? ` · ${t('K_DS_LABEL_ORDER', 'Order')} ${r.signingOrder}`
                  : '';
                const recipientName =
                  r?.name?.trim() ||
                  (r ? `${t('K_DS_RECIPIENT', 'Recipient')} ${i + 1}` : null);
                const label = recipientName
                  ? `${slotLabel} — ${recipientName}${orderSuffix}`
                  : slotLabel;
                return (
                  <option key={i + 1} value={i + 1}>
                    {label}
                  </option>
                );
              })}
            </select>
          )}
        </div>

        <div className={styles.field}>
          <label>{t('K_DS_REQUIRED', 'Required')}</label>
          <input
            type="checkbox"
            checked={field.isRequired}
            onChange={(e) => onUpdate(fieldIndex, { isRequired: e.target.checked })}
          />
        </div>

        <div className={styles.field}>
          <label>{t('K_DS_FONT_SIZE', 'Font Size')}</label>
          <input
            type="number"
            min={8}
            max={36}
            value={field.fontSize ?? 12}
            onChange={(e) => onUpdate(fieldIndex, { fontSize: Number(e.target.value) })}
          />
        </div>

        <div className={styles.row}>
          <div className={styles.field}>
            <label>{t('K_DS_WIDTH', 'Width')}</label>
            <input
              type="number"
              min={20}
              value={Math.round(field.width)}
              onChange={(e) => onUpdate(fieldIndex, { width: Number(e.target.value) })}
            />
          </div>
          <div className={styles.field}>
            <label>{t('K_DS_HEIGHT', 'Height')}</label>
            <input
              type="number"
              min={20}
              value={Math.round(field.height)}
              onChange={(e) => onUpdate(fieldIndex, { height: Number(e.target.value) })}
            />
          </div>
        </div>

        <div className={styles.row}>
          <div className={styles.field}>
            <label>{t('K_DS_LABEL_X_POSITION', 'X')}</label>
            <input
              type="number"
              value={Math.round(field.xPosition)}
              onChange={(e) => onUpdate(fieldIndex, { xPosition: Number(e.target.value) })}
            />
          </div>
          <div className={styles.field}>
            <label>{t('K_DS_LABEL_Y_POSITION', 'Y')}</label>
            <input
              type="number"
              value={Math.round(field.yPosition)}
              onChange={(e) => onUpdate(fieldIndex, { yPosition: Number(e.target.value) })}
            />
          </div>
        </div>

        {field.fieldType === 'DROPDOWN' && (
          <div className={styles.field}>
            <label>{t('K_DS_OPTIONS_COMMA', 'Options (comma-separated)')}</label>
            <input
              type="text"
              value={field.dropdownOptions ?? ''}
              onChange={(e) => onUpdate(fieldIndex, { dropdownOptions: e.target.value })}
              placeholder={t('K_DS_OPTIONS_PLACEHOLDER', 'Option 1, Option 2, Option 3')}
            />
          </div>
        )}

        {field.fieldType === 'CURRENCY' && (() => {
          const opts = field.dropdownOptions ?? '';
          const prefixLocked = opts.startsWith('!');
          const curCode = (prefixLocked ? opts.slice(1) : opts) || 'USD';
          const defaultAmt = (field.defaultValue ?? '').replace(/^[A-Z]{3}\s*/, '');
          return (
            <>
              <div className={styles.field}>
                <label>{t('K_DS_CURRENCY', 'Currency')}</label>
                <Select
                  options={CURRENCIES}
                  value={curCode}
                  onChange={(val) => {
                    onUpdate(fieldIndex, {
                      dropdownOptions: prefixLocked ? `!${val}` : val,
                      defaultValue: defaultAmt ? `${val} ${defaultAmt}` : field.defaultValue,
                    });
                  }}
                  placeholder={t('K_DS_SEARCH_CURRENCY', 'Search currency...')}
                  searchable
                  size="sm"
                />
              </div>
              <div className={styles.field}>
                <label>{t('K_DS_LOCK_CURRENCY', 'Lock currency (signer cannot change)')}</label>
                <input
                  type="checkbox"
                  checked={prefixLocked}
                  onChange={(e) => {
                    onUpdate(fieldIndex, { dropdownOptions: e.target.checked ? `!${curCode}` : curCode });
                  }}
                />
              </div>
            </>
          );
        })()}

        {field.fieldType === 'PHONE' && (() => {
          const opts = field.dropdownOptions ?? '';
          const prefixLocked = opts.startsWith('!');
          const phoneCode = (prefixLocked ? opts.slice(1) : opts) || '+1';
          const defaultNum = (field.defaultValue ?? '').replace(/^\+\d+\s*/, '');
          return (
            <>
              <div className={styles.field}>
                <label>{t('K_DS_COUNTRY_CODE', 'Country Code')}</label>
                <Select
                  options={COUNTRY_CODES}
                  value={phoneCode}
                  onChange={(val) => {
                    onUpdate(fieldIndex, {
                      dropdownOptions: prefixLocked ? `!${val}` : val,
                      defaultValue: defaultNum ? `${val} ${defaultNum}` : field.defaultValue,
                    });
                  }}
                  placeholder={t('K_DS_SEARCH_COUNTRY', 'Search country...')}
                  searchable
                  size="sm"
                />
              </div>
              <div className={styles.field}>
                <label>{t('K_DS_LOCK_COUNTRY_CODE', 'Lock country code (signer cannot change)')}</label>
                <input
                  type="checkbox"
                  checked={prefixLocked}
                  onChange={(e) => {
                    onUpdate(fieldIndex, { dropdownOptions: e.target.checked ? `!${phoneCode}` : phoneCode });
                  }}
                />
              </div>
            </>
          );
        })()}

        {!AUTHOR_UNSUPPORTED.has(field.fieldType) && (
          <>
            <div className={styles.field}>
              <label>{t('K_DS_DEFAULT_VALUE', 'Default Value')}</label>
              {field.fieldType === 'CHECKBOX' ? (
                <input
                  type="checkbox"
                  checked={field.defaultValue === 'true'}
                  onChange={(e) => setDefault(String(e.target.checked))}
                />
              ) : field.fieldType === 'CURRENCY' ? (() => {
                const opts = field.dropdownOptions ?? '';
                const curCode = (opts.startsWith('!') ? opts.slice(1) : opts) || 'USD';
                const defaultAmt = (field.defaultValue ?? '').replace(/^[A-Z]{3}\s*/, '');
                return (
                  <div className={styles.inlineInput}>
                    <span className={styles.inlinePrefix}>{curCode}</span>
                    <input
                      type="number"
                      step="0.01"
                      value={defaultAmt}
                      onChange={(e) =>
                        setDefault(e.target.value ? `${curCode} ${e.target.value}` : '')
                      }
                      placeholder={t('K_DS_PLACEHOLDER_AMOUNT', '0.00')}
                    />
                  </div>
                );
              })() : field.fieldType === 'PHONE' ? (() => {
                const opts = field.dropdownOptions ?? '';
                const phoneCode = (opts.startsWith('!') ? opts.slice(1) : opts) || '+1';
                const defaultNum = (field.defaultValue ?? '').replace(/^\+\d+\s*/, '');
                return (
                  <div className={styles.inlineInput}>
                    <span className={styles.inlinePrefix}>{phoneCode}</span>
                    <input
                      type="tel"
                      value={defaultNum}
                      onChange={(e) =>
                        setDefault(e.target.value ? `${phoneCode} ${e.target.value}` : '')
                      }
                      placeholder={t('K_DS_PHONE_NUMBER', 'Phone number')}
                    />
                  </div>
                );
              })() : field.fieldType === 'DROPDOWN' ? (
                <select
                  value={field.defaultValue ?? ''}
                  onChange={(e) => setDefault(e.target.value)}
                >
                  <option value="">{t('K_DS_NO_DEFAULT', 'No default')}</option>
                  {(field.dropdownOptions ?? '').split(',').map((opt) => {
                    const trimmed = opt.trim();
                    return trimmed ? <option key={trimmed} value={trimmed}>{trimmed}</option> : null;
                  })}
                </select>
              ) : (
                <input
                  type={
                    field.fieldType === 'NUMBER' ? 'number' :
                    field.fieldType === 'DATE' ? 'date' :
                    field.fieldType === 'EMAIL' ? 'email' :
                    'text'
                  }
                  value={field.defaultValue ?? ''}
                  onChange={(e) => setDefault(e.target.value)}
                  placeholder={
                    field.fieldType === 'NUMBER' ? '0' :
                    field.fieldType === 'EMAIL' ? t('K_DS_PLACEHOLDER_EMAIL_EXAMPLE', 'email@example.com') :
                    t('K_DS_ENTER_DEFAULT', 'Enter default value')
                  }
                />
              )}
            </div>
            <div className={styles.field}>
              <label>
                {t('K_DS_LOCK_VALUE_LABEL', 'Lock value (non-editable by {actor})').replace(
                  '{actor}',
                  actor,
                )}
                {!hasDefault && (
                  <span className={styles.fieldHint}>
                    {' '}
                    {t('K_FIELD_LOCK_NEEDS_DEFAULT', 'set a default first')}
                  </span>
                )}
              </label>
              <input
                type="checkbox"
                checked={hasDefault ? (field.isReadOnly ?? false) : false}
                disabled={!hasDefault}
                onChange={(e) => onUpdate(fieldIndex, { isReadOnly: e.target.checked })}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
}
